<!-- HomeV4.svelte - FocalPathLanding Structure with HomeV3 Marketing Content -->

<script>
  import { onMount } from "svelte";
  import RadarChart from "../FocalPath/RadarChart.svelte";
  import { push } from 'svelte-spa-router';

  let animate = $state(false);
  let showForkMan = $state(false);
  let showAppNameText = $state(false);
  let showCTAbutton = $state(false);

  // Comic images visibility state
  let comicImagesVisible = $state([false, false, false, false, false]);
  let comicImageRefs = [];

  function startAnimations() {
    animateLambdaIcon();
    setTimeout(animateForkMan, 1000);
    setTimeout(showAppName, 2000);
    setTimeout(showCTA, 3000);
  }

  function animateLambdaIcon() {
    animate = true;
  }

  function animateForkMan() {
    showForkMan = true;
  }

  function showAppName() {
    showAppNameText = true;
  }

  function showCTA() {
    showCTAbutton = true;
  }

  // Intersection Observer for comic images
  function setupComicObserver() {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = parseInt(entry.target.dataset.index);
            comicImagesVisible[index] = true;
          }
        });
      },
      {
        threshold: 0.3,
        rootMargin: '0px 0px -100px 0px'
      }
    );

    comicImageRefs.forEach((ref, index) => {
      if (ref) {
        ref.dataset.index = index;
        observer.observe(ref);
      }
    });

    return observer;
  }

  // Start the sequence of animations
  onMount(() => {
    const id = setTimeout(startAnimations, 1500);
    
    // Setup comic images observer
    const comicObserver = setupComicObserver();
    
    return () => {
      clearTimeout(id);
      comicObserver.disconnect();
    };
  });

  // Function for making the buttons navigate to the other webpage
  function goToPath() {
    push('/DeciderApp');
  }
</script>

<main>
  <div class="landing-content">
    <div class="images-external-wrapper">
      <div class="forkman-wrapper">
        <img src="Fork man.svg" alt="Fork man" class:fadeIn={showForkMan} />
      </div>
      <div class="lambda-wrapper">
        <img
          src="/Icons/Lambda icon.svg"
          alt="Lambda Icon"
          class:spin={animate}
        />
      </div>
    </div>
    <div class="appName-wrapper">
      <img
        src="Nombre tipográfico.svg"
        alt="appName"
        class:fadeIn={showAppNameText}
      />
    </div>
    <p class="app-subtitle" class:fadeIn={showAppNameText}>
      Make confident, science-backed decisions in just 10 minutes
    </p>

    <button class="cta-button" class:fadeIn={showCTAbutton} onclick={goToPath}
      >Start My First Decision</button
    >
    <p class="cta-subtext" class:fadeIn={showCTAbutton}>Built on 50+ studies in decision science.</p>
  </div>

  <!-- Problem Section -->
  <div style="width: 100%; padding: 2em; background-color: #1E1E1E; color: white;">
    <h2 style="font-size: 2rem; margin-bottom: 1em; text-align: center;">When every choice feels like a gamble, progress stops.</h2>
    <p style="margin-top:1.5em;">You've been there — second-guessing a career move, a hiring decision, a partnership.</p>
    <p style="margin-top:1.5em;">We call it <strong>decision fatigue</strong>. Endless pros and cons lists, gut feelings that shift daily, advice that only adds noise.</p>
    <p style="margin-top:3em; font-style: italic; color: #9cfffa;">It's not your fault — your brain isn't wired for modern complexity.</p>
  </div>

  <!-- Solution Section with RadarChart -->
  <div class="section image-text">
    <div class="container">
      <h1 style="font-size: 200%; margin-bottom: 50px; text-align: center;">Focal Path brings the science of great decisions to your screen.</h1>
      <div class="columns is-vcentered">
        <div class="column is-8">
          <RadarChart
            categories={[
              {name: "Trascendence", type: "Critical"},
              {name: "Impact on relationships", type: "Critical"},
              {name: "Cost", type: "Critical"},
              {name: "Long term benefit", type: "Critical"},
              {name: "Risk", type: "Critical"}
            ]}
            paths={[
              { name: "", values: [1, 5, 3, 3, 4], color: "#CBBAED" },
              { name: "", values: [5, 3, 4, 1.5, 3], color: "#BF1363" },
            ]}
          />
        </div>
        <div class="column is-4">
          <p>
            <strong>Focal Path</strong> structures your thinking when facing <strong>high stakes choices</strong> using proven decision science frameworks.
          </p>
        </div>
      </div>
    </div>
  </div>

  <!-- How It Works Section -->
  <div class="section image-text">
    <div class="container">
      <h1 style="font-size: 200%; margin-bottom: 50px;">How it works</h1>

      <!-- TODO: Replace with new 3-step process diagram -->
      <div class="placeholder-diagram" style="background: #f0f0f0; padding: 3rem; border-radius: 10px; text-align: center; margin-bottom: 2rem;">
        <p style="color: #666; font-size: 1.1rem;">📊 [DIAGRAM PLACEHOLDER]</p>
        <p style="color: #666;">3-Step Process: Define Challenge → Structure Reasoning → See Clarity Score</p>
      </div>

      <div class="how-it-works-steps">
        <div class="step-card">
          <div class="step-number">1</div>
          <h3>Define your challenge</h3>
          <p>Pick a template (career, business, life).</p>
        </div>
        <div class="step-card">
          <div class="step-number">2</div>
          <h3>Structure your reasoning</h3>
          <p>Our guided prompts and weighting system reveal what truly matters.</p>
        </div>
        <div class="step-card">
          <div class="step-number">3</div>
          <h3>See your clarity score</h3>
          <p>Visual dashboards show your best-fit option and potential biases.</p>
        </div>
      </div>
    </div>
  </div>

  <!-- Proof Section -->
  <div style="width: 100%; padding: 3em; background-color: #1E1E1E; color: white;">
    <div class="container">
      <h2 style="font-size: 2rem; margin-bottom: 2em; text-align: center;">Grounded in behavioural science — proven 6× more effective.</h2>
      
      <div class="proof-grid">
        <div class="proof-item">
          <!-- TODO: Add McKinsey logo placeholder -->
          <div class="proof-icon">🏢</div>
          <p>Based on frameworks used by McKinsey and leading decision researchers.</p>
        </div>
        <div class="proof-item">
          <div class="proof-icon">🧠</div>
          <p>Inspired by behavioural economics, not intuition.</p>
        </div>
        <div class="proof-item">
          <div class="proof-icon">📊</div>
          <p>Tested on real-world cases — from career switches to business strategy.</p>
        </div>
      </div>

      <div class="testimonial-placeholder" style="background: rgba(255,255,255,0.1); padding: 2rem; border-radius: 10px; margin-top: 2rem; text-align: center;">
        <p style="font-style: italic; font-size: 1.1rem;">"What makes a decision great is not that it has a great outcome. A great decision is the result of a good process."</p>
        <cite style="opacity: 0.8;">— Decision Science Research</cite>
      </div>
    </div>
  </div>

  <!-- Speed & Ease Section -->
  <div class="section">
    <div class="container">
      <h1 style="font-size: 200%; margin-bottom: 50px; text-align: center;">Clarity before your next coffee break.</h1>
      
      <div class="columns">
        <div class="column">
          <div class="has-text-centered">
            <div class="benefit-icon">⏱️</div>
            <h4 class="title is-4" style="padding: 20px;">10 Minutes</h4>
          </div>
          <div class="box">
            Takes 10 minutes to reach structured insight.
          </div>
        </div>
        
        <div class="column">
          <div class="has-text-centered">
            <div class="benefit-icon">🎯</div>
            <h4 class="title is-4" style="padding: 20px;">No Coaching</h4>
          </div>
          <div class="box">
            No coaching calls, no long courses — just guided clarity.
          </div>
        </div>
        
        <div class="column">
          <div class="has-text-centered">
            <div class="benefit-icon">🔒</div>
            <h4 class="title is-4" style="padding: 20px;">Private</h4>
          </div>
          <div class="box">
            Simple, visual, and private.
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Pricing Section -->
  <div style="width: 100%; padding: 3em; background-color: #f8f9fa;">
    <div class="container">
      <h2 style="font-size: 2rem; margin-bottom: 2em; text-align: center; color: #333;">Choose your path to better decisions.</h2>
      
      <div class="pricing-grid">
        <div class="pricing-card">
          <h3>Free Starter</h3>
          <div class="price">Free</div>
          <ul>
            <li>Test one decision</li>
            <li>Basic clarity flow</li>
          </ul>
          <button onclick={goToPath}>Get Started</button>
        </div>
        
        <div class="pricing-card featured">
          <div class="popular-badge">Most Popular</div>
          <h3>Pro</h3>
          <div class="price">€12<span>/month</span></div>
          <p class="subtitle">Independent professionals</p>
          <ul>
            <li>Unlimited decisions</li>
            <li>Bias checks</li>
            <li>AI summaries</li>
          </ul>
          <button onclick={goToPath}>Start Pro Trial</button>
        </div>
        
        <div class="pricing-card">
          <h3>Executive</h3>
          <div class="price">€39<span>/month</span></div>
          <p class="subtitle">Leaders & business owners</p>
          <ul>
            <li>Scenario simulator</li>
            <li>Export reports</li>
            <li>Advanced analytics</li>
          </ul>
          <button onclick={goToPath}>Contact Sales</button>
        </div>
        
        <div class="pricing-card">
          <h3>Teams</h3>
          <div class="price">Custom</div>
          <p class="subtitle">Small business use</p>
          <ul>
            <li>Shared dashboards</li>
            <li>Group decisions</li>
            <li>Team analytics</li>
          </ul>
          <button onclick={goToPath}>Contact Us</button>
        </div>
      </div>
      
      <p style="text-align: center; color: #666; margin-top: 2rem;">Cancel anytime • 30-day money-back guarantee</p>
    </div>
  </div>

  <!-- Final CTA Section -->
  <section>
    {#if showAppNameText}
    <div style="margin-top: 5em;">
      <img
        src="Nombre tipográfico.svg"
        alt="appName"
        class:fadeIn={showAppNameText}
      />
    </div>
    {/if}
    <h2 style="font-size: 3rem; margin: 2rem 0;">Stop overthinking. Start deciding.</h2>
    
    <button class="second-cta-button" style="margin: 1em;" onclick={goToPath}>Start My Free Decision Now</button>
    <p class="second-cta-subtext">Clarity, confidence, and control — in one simple session.</p>
  </section>

  <!-- Examples, capabilities, limitations -->
  <section class="section">
    <div class="container">
      <div class="columns">
        <!-- First Column - Examples -->
        <div class="column">
          <div class="has-text-centered">
            <img
              src="/Icons/Examples Icon 2.svg"
              alt="Examples Icon"
              class="icon is-large"
            />
            <h4 class="title is-4" style="padding: 20px;">Examples</h4>
          </div>
          <div class="box">
            <b>Should I quit my job</b><br />
            to pursue a more fulfilling career?
          </div>
          <div class="box">
            <b>Should I start investing in crypto?</b><br /> or keep saving for a
            house
          </div>
          <div class="box">
            <b>Help me prioritize projects</b><br /> to discard distractions and
            get real traction
          </div>
        </div>

        <!-- Second Column - Capabilities -->
        <div class="column">
          <div class="has-text-centered">
            <img
              src="/Icons/Capabilities Icon.svg"
              alt="Capabilities Icon"
              class="icon is-large"
            />
            <h4 class="title is-4" style="padding: 20px;">Capabilities</h4>
          </div>
          <div class="box">
            <b>Prevent biases</b><br /> from misleading you and separate skill from
            chance
          </div>
          <div class="box">
            <b>Learn to observe your emotions</b><br /> and expand your perspective
          </div>
          <div class="box">
            <b>Have peace of mind</b><br /> you made the best choice with the information
            you had
          </div>
        </div>

        <!-- Third Column - Limitations -->
        <div class="column">
          <div class="has-text-centered">
            <img
              src="/Icons/Limitations icon.svg"
              alt="Limitations Icon"
              class="icon is-large"
            />
            <h4 class="title is-4" style="padding: 20px;">Limitations</h4>
          </div>
          <div class="box">
            <b>Will not decide for you</b> and will require some reflection
          </div>
          <div class="box">
            You will still need a <b>clear goal</b> to make a good decision
          </div>
          <div class="box">
            You will still need <b>good information</b> to make a good decision
          </div>
        </div>
      </div>
    </div>
  </section>

  <!--  Footer -->
  <footer class="footer" style="margin-top: 2em;">
    <div
      class="content has-text-centered"
      style="margin-left: 2em; margin-right: 2em"
    >
      <p class="footer-tagline" style="font-size: 1.1rem; margin-bottom: 1.5rem;">
        Built by decision-science researchers and product designers.
      </p>
      
      <!-- Newsletter Signup -->
      <div class="newsletter" style="margin-bottom: 2rem;">
        <h4>Stay updated on decision science</h4>
        <div style="display: flex; justify-content: center; gap: 0.5rem; margin-top: 1rem;">
          <input type="email" placeholder="Enter your email" style="padding: 0.8rem; border-radius: 25px; border: 1px solid #ccc;" />
          <button style="background: #9cfffa; color: #084b83; padding: 0.8rem 1.5rem; border-radius: 25px; border: none;">Subscribe</button>
        </div>
      </div>

      <p>
        <strong>Focal Path</strong> by
        <a href="https://forms.gle/PD7tAohbUq83FK4k9">Antonio Martin</a>. The
        source code is licensed
        <a href="http://opensource.org/licenses/mit-license.php">MIT</a>. The
        website content is licensed
        <a href="http://creativecommons.org/licenses/by-nc-sa/4.0/"
          >CC BY NC SA 4.0</a
        >.
      </p>
      <hr />
      <div class="references" style="font-size: small;">
        <p style="padding: 1em;">
          Focal Path is based on Prospect Theory, Behavioral Economics, and
          Cognitive Psychology research
        </p>
      </div>
    </div>
  </footer>
</main>

<style>
  /* Inherit all base styles from FocalPathLanding */

  main {
    display: flex;
    flex-direction: column;
    align-items: center;
    position: relative;
    text-align: center;
  }

  .section img {
    border-radius: 5px;
  }

  button {
    background-color: #9cfffa;
    color: #084b83;
    border: none;
    padding: 10px 20px;
    border-radius: 5px;
    font-size: 1em;
    cursor: pointer;
    transition: background-color 0.3s ease;
  }

  .cta-button {
    position: absolute;
    bottom: 5em;
    opacity: 0;
  }

  .cta-subtext {
    position: absolute;
    bottom: 2.5em;
    left: 50%;
    transform: translateX(-50%);
    opacity: 0;
    font-size: 0.95em;
    color: gray;
    max-width: 90vw;
    text-align: center;
    line-height: 1.3;
  }

  .second-cta-button {
    font-size: 1.2em;
    padding: 12px 24px;
  }

  .second-cta-subtext {
    font-size: 0.95em;
    color: gray;
    text-align: center;
    margin: 0.5em auto 2em auto;
    max-width: 90vw;
    line-height: 1.3;
  }

  button:hover,
  button:focus {
    background-color: #ffec6c;
  }

  .image-text {
    margin-top: 0em;
  }

  .image-text .column {
    display: flex;
    flex-direction: column;
    text-align: left;
    min-height: 100%;
  }


  .landing-content {
    position: relative;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    min-height: 100vh;
    max-width: 100%;
  }

  .column {
    padding: 40px;
  }

  .images-external-wrapper {
    display: flex;
  }

  img[alt="Lambda Icon"] {
    width: 95px;
  }

  .spin {
    animation: spinAndFlatten 0.5s forwards;
  }

  @keyframes spinAndFlatten {
    from {
      transform: rotate(0deg) scale(1, 1);
    }
    to {
      transform: rotate(248deg) scale(0.5, 1.15);
    }
  }

  img[alt="Fork man"] {
    opacity: 0;
    height: 110px;
    width: 100px;
  }

  img[alt="appName"] {
    opacity: 0;
    height: 65px;
    width: auto;
    min-width: 300px;
    max-width: 90vw;
  }

  .fadeIn {
    animation: fadeIn 0.5s forwards;
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  .appName-wrapper {
    margin: 10em;
    margin-top: 5em;
    left: 50vw;
  }

  .app-subtitle {
    position: absolute;
    margin-top: 15em;
    font-size: 1.2em;
    color: gray;
    opacity: 0;
  }

  /* New styles for HomeV4 specific elements */
  .how-it-works-steps {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 2rem;
    margin-top: 2rem;
  }

  .step-card {
    background: white;
    padding: 2rem;
    border-radius: 10px;
    box-shadow: 0 4px 15px rgba(0,0,0,0.1);
    text-align: center;
  }

  .step-number {
    background: #9cfffa;
    color: #084b83;
    width: 50px;
    height: 50px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    font-size: 1.2rem;
    margin: 0 auto 1rem auto;
  }

  .proof-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 2rem;
  }

  .proof-item {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 1.5rem;
    background: rgba(255,255,255,0.1);
    border-radius: 10px;
  }

  .proof-icon {
    font-size: 2rem;
    flex-shrink: 0;
  }

  .benefit-icon {
    font-size: 3rem;
    margin-bottom: 1rem;
  }

  .pricing-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 2rem;
    margin-bottom: 2rem;
  }

  .pricing-card {
    background: white;
    border-radius: 10px;
    padding: 2rem;
    text-align: center;
    box-shadow: 0 4px 15px rgba(0,0,0,0.1);
    position: relative;
  }

  .pricing-card.featured {
    border: 3px solid #9cfffa;
    transform: scale(1.05);
  }

  .popular-badge {
    position: absolute;
    top: -10px;
    left: 50%;
    transform: translateX(-50%);
    background: #9cfffa;
    color: #084b83;
    padding: 0.5rem 1rem;
    border-radius: 20px;
    font-size: 0.9rem;
    font-weight: 600;
  }

  .pricing-card h3 {
    font-size: 1.5rem;
    margin-bottom: 0.5rem;
    color: #333;
  }

  .price {
    font-size: 2rem;
    font-weight: 700;
    color: #084b83;
    margin-bottom: 0.5rem;
  }

  .price span {
    font-size: 1rem;
    color: #666;
  }

  .subtitle {
    color: #666;
    margin-bottom: 1.5rem;
    font-size: 0.9rem;
  }

  .pricing-card ul {
    list-style: none;
    padding: 0;
    margin: 0 0 2rem 0;
  }

  .pricing-card li {
    padding: 0.5rem 0;
    color: #666;
  }

  .pricing-card li:before {
    content: "✓";
    color: #9cfffa;
    font-weight: bold;
    margin-right: 0.5rem;
  }

  .placeholder-diagram {
    border: 2px dashed #ccc;
  }

  :global(body) {
    font-family: "IBM Plex Mono", monospace;
  }

  /* Responsive adjustments */
  @media (max-width: 768px) {
    .how-it-works-steps {
      grid-template-columns: 1fr;
    }
    
    .pricing-grid {
      grid-template-columns: 1fr;
    }
    
    .pricing-card.featured {
      transform: none;
    }
  }
</style>
